import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import RosettaFormattedMessage from '../common/rosetta_formatted_message';
import Section from './section';
import Ad from '../common/ad';
import NativeAd from '../common/native_ad';
import MediaQuery from '../common/media_query';
import ProductCarousel from '../article/products/product_carousel';
import withScrollDepthTracking from '../hocs/withScrollDepthTracking';
import { scrollAdPosition } from '../../utils/ads/positionStrings';
import {
  delay,
  loadScriptInHead,
  nodeYScrollCoord,
  scrollTo
} from '../../utils/dom';
import { getSectionContainerClassNames, allParagraphs } from '../../utils/sections';
import RelatedEntries from '../article/related_entries';
import MostPopular from '../article/most_popular';
import SuperSaleCard from '../card/super_sale_card';
import { SUPER_SALE_MODULE_DATA as superSale } from '../module/super_sale_data';
import { getTodaysSuperSale } from '../../utils/super_sale';
import { EDITION_ID_EN_US } from '../../constants';
import { get, isEmpty, map, omitBy, trim } from 'lodash';
import TrinityAudioPlayer from '../common/trinityAudioPlayer';

const HORIZONTAL_SCROLL_PX = 0;
const MOBILE_HEADER_HEIGHT_PX = 40;
const SLIDE_SCROLL_DELAY_MS = 1500;
const HANDLE_SCROLL_THROTTLE_MS = 1000;

const scrollToNode = (node) => scrollTo(
  HORIZONTAL_SCROLL_PX,
  nodeYScrollCoord(node, window) - MOBILE_HEADER_HEIGHT_PX
);

export class Sections extends Component {
  constructor(props, context) {
    super(props, context);
    this._nodes = new Array(this.props.sections.length);
  }

  componentDidMount() {
    const { scrollWait, slideIndex } = this.props;
    if (slideIndex > 0) {
      delay(
        () => this.scrollToSlide(slideIndex),
        scrollWait
      );
    }

    if (this.shouldRenderActionButtonContainer()) {
      loadScriptInHead('marketing', 'https://embed.actionbutton.co/widget/widget.min.js');
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.slideIndex === this.props.slideIndex) {
      return;
    }
    this.scrollToSlide(nextProps.slideIndex);
  }

  getInlineAd = (adPosition) => {
    if (isEmpty(adPosition)) {
      return;
    }

    const { adsState, adSizes, onRightRailArticle, onFullWidthArticle } = this.props;



    if (get(adsState, 'ad_data.ad_disable_all_ads')) {
      return;
    }

    const adProps = {
      adsState,
      uniqueId: `section-ad-${adPosition}`,
      position: adPosition
    };

    const nonEmptyAdSizes = omitBy(adSizes, isEmpty);

    const isArticleType = onRightRailArticle || onFullWidthArticle;
    const disableAdRefresh = !!get(adsState, 'ad_data.ad_disable_ad_refresh');
    return map(nonEmptyAdSizes, (sizes, breakpoint) => (
      <MediaQuery key={breakpoint} breakpoint={breakpoint}>
        <div className="section-ad">
          <RosettaFormattedMessage parentFilename={__filename}
            id="ad"
            description="Copy for above Section Ads"
            defaultMessage="Advertisement"
          />
          <Ad {...adProps}
            sizes={sizes}
            refreshOnInterval={
              isArticleType && breakpoint === 'desktop' && !disableAdRefresh
            }
            scrollAdPosition={scrollAdPosition(adPosition)}
            onFullWidthArticle={onFullWidthArticle}
          />
        </div>
      </MediaQuery>
    ));
  };

  scrollToSlide = (slideIndex) => {
    if (slideIndex >= this.props.sections.length) {
      return;
    }
    const nodeRef = this._nodes[slideIndex - 1];
    const scrollFn = () => scrollToNode(nodeRef);
    scrollFn();
    delay(scrollFn, this.props.scrollWait);
  };

  /*
   * Returns the index of the section where
   * we should place the RelatedEntries after.
   *
   * Right-Rail
   *   - 0 paragraphs, don't insert
   *   - 1 to 4 paragraphs, insert below the last section
   *   - 5 paragraphs or more, insert below the 3rd paragraph
   *
   * Full-Width
   *   - Insert after last paragraph
   *
   * Mobile Slideshow
   *   - Insert after 0th slideshow section (not intro body section)
   */
  relatedEntrySectionIndex = (allParagraphs) => {
    if (isEmpty(this.props.relatedEntries) && this.props.onFullWidthArticle) {
      return null;
    }

    if (this.props.renderAsMobileSlideSections) {
      return 0;
    }

    if (this.props.onFullWidthArticle) {
      return this.props.sections.length - 1;
    }

    if (this.props.onRightRailArticle) {
      if (allParagraphs.length === 0) {
        return null;
      }

      return allParagraphs.length > 4
        ? allParagraphs[2].index
        : this.props.sections.length - 1;
    }

    return null;
  };

  /*
   * Returns the index of the section where we
   * will handle rendering of Most Popular
   * Most Popular Placement:
   *
   * RR
   *  - Related Entries
   *   - 7 paragraphs or more, insert below the 6th paragraph
   *   - 6 paragraphs or less, do not insert
   *
   *  - No Related Entries
   *    - 5 paragraphs or more, insert below the 3rd paragraph.
   *    - 4 paragraphs or less, insert below the last section.
   *
   * FW
   *  - Related Entries
   *    - Do not insert
   *  - No Relatd Entries
   *    - Insert after last section
   *
   * SS
   *  - Mobile
   *    - 4 paragraphs or more, insert below the 3rd section.
   *    - 3 paragraphs or less, do not insert.
   *  - Desktop
   *    - Do not show
   */
  mostPopularSectionIndex = (allParagraphs) => {
    if (isEmpty(this.context.edition_data.most_popular)) {
      return null;
    }

    if (this.props.onFullWidthArticle) {
      if (isEmpty(this.props.relatedEntries)) {
        return this.props.sections.length - 1;
      }
    }

    if (this.props.onRightRailArticle) {
      if (isEmpty(this.props.relatedEntries)) {
        return allParagraphs.length > 4
          ? allParagraphs[2].index
          : this.props.sections.length - 1;
      }

      return allParagraphs.length > 6
        ? allParagraphs[5].index
        : null;
    }

    if (this.props.renderAsMobileSlideSections
      && this.props.sections.length > 4) {
      return 3;
    }

    return null;
  };

  /*
   * Returns the index of the section where we
   * will handle rendering of the Super Sale.
   *
   * Needs 5 paragraphs to show
   *
   * SS
   *  - Insert after 4th slide on mobile
   *  RR
   *  - Insert after 2nd paragraph slide on web / mobile
   */

  superSaleSectionIndex = (allParagraphs) => {

    if (this.props.renderAsMobileSlideSections
      && this.props.sections.length > 4) {
      return 3;
    }

    if (this.props.onRightRailArticle) {
      return allParagraphs.length > 2
        ? allParagraphs[1].index
        : null;
    }

    return null;
  }

  /*
   * Renders the given section as a Section. Also renders all of that section's
   * after-section content, like Inline Ads, Most Popular, and Related Entries.
   */
  toSection = (section, allParagraphs, index) => {
    const ad_sponsorship = get(this.props.adsState, 'ad_data.ad_sponsorship', '');

    let nativeAd;
    let relatedEntries;
    if (index === this.relatedEntrySectionIndex(allParagraphs)) {
      relatedEntries = isEmpty(this.props.relatedEntries)
        ? null
        : (
          <RelatedEntries
            relatedEntries={this.props.relatedEntries}
            onRightRailArticle={this.props.onRightRailArticle}
            onSlideshowArticle={this.props.onSlideshowArticle}
          />
        );
      nativeAd = this.props.onFullWidthArticle || trim(ad_sponsorship).length > 0
        ? relatedEntries
        : (
          <NativeAd
            adsState={this.props.adsState}
            uniqueId={'native-add'}
            position={'native'}
            className="native-ad"
            lazyLoadCushionPx={600}
            fallbackElement={relatedEntries}
          />
        );
    }

    let mostPopular;
    if (index === this.mostPopularSectionIndex(allParagraphs)) {
      mostPopular = (
        <MediaQuery breakpoint="notDesktop">
          <MostPopular mostPopular={this.context.edition_data.most_popular} />
        </MediaQuery>
      );
    }

    let polyptychProductCarousel;
    if (section.polyptychAttributes) {
      polyptychProductCarousel = <ProductCarousel {...this.props} {...section} />;
    }

    // Only show super sale if index exists and article is a US shopping article and not sponsored
    let superSaleCard;
    if (
      index === this.superSaleSectionIndex(allParagraphs)
      && !ad_sponsorship
      && this.props.isShoppingArticle
      && this.context.edition_data.id === EDITION_ID_EN_US
    ) {
      const todaysSuperSale = getTodaysSuperSale(superSale.rows[0].entities);
      if (todaysSuperSale) {
        const superSaleType = this.props.renderAsMobileSlideSections ? 'slideshow' : 'right-rail';
        superSaleCard = <SuperSaleCard {...todaysSuperSale} type={superSaleType} />;
      }
    }

    return (
      <Fragment key={index}>
        <div className="section-outer-container"
          ref={node => this._nodes[index] = node}
        >
          <div className={getSectionContainerClassNames(section)}>
            <Section
              {...this.props}
              {...section}
              isMobileSlideSection={this.props.renderAsMobileSlideSections}
              onRightRailArticle={this.props.onRightRailArticle}
              product_layout={this.props.product_layout || false}
              sectionIndex={index}
              sectionsLength={this.props.sections.length}
              sold_content_type={this.props.sold_content_type}
            />
          </div>
        </div>
        <div className="after-section-content">
          {this.getInlineAd(section.adPosition)}
          {nativeAd}
          {mostPopular}
          {polyptychProductCarousel}
          {superSaleCard}
        </div>
      </Fragment>
    );
  };

  /*
   * If an editor has inserted their own action button an HTML section then we
   * don't need to manually render an action button container, as there will
   * already be one in the DOM.
   *
   * If the article is a slideshow then don't render an action button container
   * at all and don't load the 3rd party action button script.
   */
  shouldRenderActionButtonContainer = () => {
    if (this.props.onSlideshowArticle) {
      return false;
    }

    return !this.props.sections.find((section) =>
      get(section, 'content.resource') &&
      section.content.resource.includes('action_button_container')
    );
  };

  /*
   * We usually append one action button container as the very last section.
   * The action button script auto-fills this container with an action button.
   * The script leaves the container blank when the page needs no button.
   */
  actionButtonSection = () => {
    if (this.shouldRenderActionButtonContainer()) {
      return (
        <div className="section-outer-container">
          <div id="action_button_container" />
        </div>
      );
    }
  };

  render() {
    return (
      <section className="body">
        <TrinityAudioPlayer className={this.props.onRightRailArticle ? 'trinity-left-column' : ''}/>
        {
          map(this.props.sections, (section, index) => [
            this.toSection(
              section,
              allParagraphs(this.props.sections),
              index
            )
          ])
        }
        {this.actionButtonSection()}
      </section>
    );
  }
}

const decorator = withScrollDepthTracking({
  shouldTrackScrollEvent() {
    return this.props.shouldSendGAScrollDepthEvents;
  }
});

export default decorator(Sections);

const AdSizesType = PropTypes.arrayOf(
  PropTypes.arrayOf(
    PropTypes.number.isRequired
  )
);

Sections.propTypes = {
  adsState: PropTypes.object.isRequired,
  adSizes: PropTypes.shape({
    desktop: AdSizesType,
    tablet: AdSizesType,
    mobile: AdSizesType
  }).isRequired,
  product_layout: PropTypes.bool,
  hide_product_info_button: PropTypes.bool,
  renderAsMobileSlideSections: PropTypes.bool.isRequired,
  scrollWait: PropTypes.number.isRequired,
  sections: PropTypes.arrayOf(
    PropTypes.object.isRequired
  ).isRequired,
  shouldSendGAScrollDepthEvents: PropTypes.bool.isRequired,
  slideIndex: PropTypes.number,
  throttleWait: PropTypes.number.isRequired,
  sold_content_type: PropTypes.string,
  onSlideshowArticle: PropTypes.bool,
  onRightRailArticle: PropTypes.bool,
  onFullWidthArticle: PropTypes.bool,
  relatedEntries: PropTypes.array,
  isShoppingArticle: PropTypes.bool
};

Sections.contextTypes = {
  edition_data: PropTypes.object
};

Sections.defaultProps = {
  adsState: {},
  adSizes: {},
  renderAsMobileSlideSections: false,
  scrollWait: SLIDE_SCROLL_DELAY_MS,
  sections: [],
  throttleWait: HANDLE_SCROLL_THROTTLE_MS
};
